import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

// Root-level Emergent Google-Auth callback.
// Emergent returns the browser to `<origin>/<any-real-app-route>` with
// `#session_id=<opaque>` appended to the URL fragment. Per the Emergent
// Auth playbook, detection MUST run at the router root (before Routes) and
// SYNCHRONOUSLY during render (via useLocation().hash which is reactive).
// The one-time exchange is then fired from useEffect, guarded by a useRef
// so React StrictMode's double-invocation cannot double-consume the code.
export default function SessionIdHandler() {
  const location = useLocation();
  const navigate = useNavigate();
  const { finishGoogleSession } = useAuth();
  const consumed = useRef(false);

  const hasSessionId = (location.hash || "").includes("session_id=");

  useEffect(() => {
    if (!hasSessionId || consumed.current) return;
    consumed.current = true;

    const raw = (location.hash || "").replace(/^#/, "");
    const params = new URLSearchParams(raw);
    const sessionId = params.get("session_id");

    if (!sessionId) {
      toast.error("Sign-in didn't complete. Please try again.");
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }

    (async () => {
      try {
        const user = await finishGoogleSession(sessionId);
        toast.success(`Welcome, ${user.name?.split(" ")[0] || "friend"}!`);
        const roles = user?.roles || [];
        const mode = user?.current_mode || (roles.includes("owner") ? "owner" : "camper");
        const nextPath = mode === "owner" ? "/owner" : "/passport";
        // Strip the session_id from the URL before navigating so it doesn't
        // leak anywhere (analytics, history, screenshots).
        window.history.replaceState({}, "", window.location.pathname);
        navigate(nextPath, { replace: true });
      } catch (e) {
        const detail = e?.response?.data?.detail || e?.message || "Sign-in failed.";
        toast.error(`Google sign-in failed: ${detail}`);
        // Log the full error to the console so we can inspect it in
        // devtools if the toast is missed.
        // eslint-disable-next-line no-console
        console.error("[EmergentAuth]", { detail, error: e });
        window.history.replaceState({}, "", window.location.pathname);
        navigate("/login", { replace: true });
      }
    })();
  }, [hasSessionId, location.hash, finishGoogleSession, navigate]);

  return null;
}
